import { type FC, useEffect } from "react";

import { useQuery } from "@apollo/client/react";

import { AssistantNoteDocument } from "@app_schema";

import {
  NoteEmpty,
  NoteRow,
  NoteSection,
  NotesCount,
  NotesSheet,
} from "@styled/assistant_notes";

import { type AssistantNotes } from "./use_assistant_notes";

// The count on the assistant's line; it opens and closes the notes.
export const AssistantNotesCount: FC<{ notes: AssistantNotes }> = ({ notes }) =>
  notes.sections.length > 0 && (
    <NotesCount
      count={notes.sections.length}
      state={notes.state}
      disabled={notes.locked}
      tip={notes.locked ? "Disabled while the assistant is working" : undefined}
      onClick={notes.toggle}
    />
  );

// One open note's points, loaded alone. Loaded again when the list says the
// note changed since (while open, or while closed and cached).
const AssistantNoteBody: FC<{
  projectId: string;
  noteKey: string;
  updatedAt: string;
}> = ({ projectId, noteKey, updatedAt }) => {
  const { data, refetch } = useQuery(AssistantNoteDocument, {
    variables: { id: projectId, key: noteKey },
  });
  const note = data?.project.assistantNote;
  const stale = note !== undefined && note?.updatedAt !== updatedAt;
  useEffect(() => {
    if (stale) void refetch();
  }, [stale, refetch]);

  if (note?.rows.length === 0) return <NoteEmpty />;

  return note?.rows.map((row, index) => (
    <NoteRow key={index} label={row.label} lines={row.lines} />
  ));
};

// The notes over the letters or pages: one read-only sheet, a section a note.
export const AssistantNotesView: FC<{ notes: AssistantNotes }> = ({
  notes,
}) => (
  <NotesSheet>
    {notes.sections.map((section) => (
      <NoteSection
        key={section.key}
        title={section.title}
        rewritten={section.rewritten}
        updatedAt={section.updatedAt}
        readAt={notes.readAt(section.key)}
        current={section.key === notes.currentKey}
        expanded={notes.expanded(section.key)}
        onToggle={() => notes.toggleNote(section.key)}
      >
        <AssistantNoteBody
          projectId={notes.projectId}
          noteKey={section.key}
          updatedAt={section.updatedAt}
        />
      </NoteSection>
    ))}
  </NotesSheet>
);
